export type NormalizedCropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ImageLayout = {
  x: number;
  y: number;
  width: number;
  height: number;
  crop?: { x: number; y: number; width: number; height: number };
};

export const clampNormalizedCropRect = (input: Partial<NormalizedCropRect> | null | undefined): NormalizedCropRect | null => {
  if (!input) return null;
  const rawX = Number(input.x ?? 0);
  const rawY = Number(input.y ?? 0);
  const rawWidth = Number(input.width ?? 1);
  const rawHeight = Number(input.height ?? 1);
  if (!Number.isFinite(rawX) || !Number.isFinite(rawY) || !Number.isFinite(rawWidth) || !Number.isFinite(rawHeight)) {
    return null;
  }

  const x = Math.max(0, Math.min(1, rawX));
  const y = Math.max(0, Math.min(1, rawY));
  const width = Math.max(0.01, Math.min(1 - x, rawWidth));
  const height = Math.max(0.01, Math.min(1 - y, rawHeight));
  return { x, y, width, height };
};

export const applyCropPanDelta = (
  crop: NormalizedCropRect | null,
  deltaX: number,
  deltaY: number,
): NormalizedCropRect | null => {
  if (!crop) return null;
  const width = Math.max(0.01, Math.min(1, crop.width));
  const height = Math.max(0.01, Math.min(1, crop.height));
  const nextX = Math.max(0, Math.min(1 - width, crop.x + deltaX));
  const nextY = Math.max(0, Math.min(1 - height, crop.y + deltaY));
  return { x: nextX, y: nextY, width, height };
};

export const computeImageLayout = (input: {
  width: number;
  height: number;
  fit: 'cover' | 'contain';
  sourceWidth: number;
  sourceHeight: number;
  crop?: NormalizedCropRect | null;
}): ImageLayout => {
  const { width, height, fit, sourceWidth, sourceHeight, crop } = input;
  const safeSourceWidth = sourceWidth || 1;
  const safeSourceHeight = sourceHeight || 1;
  const safeCrop = clampNormalizedCropRect(crop);

  if (safeCrop) {
    return {
      x: 0,
      y: 0,
      width,
      height,
      crop: {
        x: safeCrop.x * safeSourceWidth,
        y: safeCrop.y * safeSourceHeight,
        width: safeCrop.width * safeSourceWidth,
        height: safeCrop.height * safeSourceHeight,
      },
    };
  }

  if (fit === 'contain') {
    const scale = Math.min(width / safeSourceWidth, height / safeSourceHeight);
    const renderWidth = safeSourceWidth * scale;
    const renderHeight = safeSourceHeight * scale;
    return {
      x: (width - renderWidth) / 2,
      y: (height - renderHeight) / 2,
      width: renderWidth,
      height: renderHeight,
    };
  }

  const targetRatio = width / height;
  const sourceRatio = safeSourceWidth / safeSourceHeight;

  if (sourceRatio > targetRatio) {
    const cropWidth = safeSourceHeight * targetRatio;
    return {
      x: 0,
      y: 0,
      width,
      height,
      crop: {
        x: (safeSourceWidth - cropWidth) / 2,
        y: 0,
        width: cropWidth,
        height: safeSourceHeight,
      },
    };
  }

  const cropHeight = safeSourceWidth / targetRatio;
  return {
    x: 0,
    y: 0,
    width,
    height,
    crop: {
      x: 0,
      y: (safeSourceHeight - cropHeight) / 2,
      width: safeSourceWidth,
      height: cropHeight,
    },
  };
};
